import { useMemo } from 'react';
import { buildSingleMatchesArchiveEntry, liveAllPlayersOf } from '@starpoint/engine';
import { PLAYER_DB, getEffectivePlayerDB } from '../engine/playerDb';
import { seedFallbackPlayers } from '../lib/api/players';

// ─────────────────────────────────────────────────────────────────────
// useDerivations — the cross-slice derived data (memoised) for the app shell,
// lifted verbatim out of Padel24AppMain (extracted from the former monolith) as a god-component
// collapse slice (PR-AX). All five are PURE useMemo computations (no Storage,
// no effects) over the state slices passed in via `deps`:
//   - liveAllPlayers   : allPlayers + accumulated ELO/matchesPlayed deltas from
//                        the active + paused tournaments (par-aware for KM/serie)
//   - effectivePlayerDB: getEffectivePlayerDB(archive, liveAllPlayers)
//   - combinedArchive  : archive + a virtual "single matches" tournament
//   - archiveWithLive  : combinedArchive + active/paused tournaments + active serie
//   - archiveForArchive: archive-view source (virtual single matches + live KM +
//                        seriespill per class+season)
// liveAllPlayers is returned first so the caller can feed it into
// useSingleMatchActions (which runs after this hook in Padel24AppMain).
// ─────────────────────────────────────────────────────────────────────
export interface UseDerivationsDeps {
  allPlayers: any;
  tournament: any;
  pausedTournaments: any;
  archive: any;
  singleMatches: any;
  activeSeries: any;
}

export function useDerivations({
  allPlayers,
  tournament,
  pausedTournaments,
  archive,
  singleMatches,
  activeSeries,
}: UseDerivationsDeps) {
  // Moved to @starpoint/engine (#217): the server finalises single matches and
  // must calculate against the SAME list, so the computation can no longer live
  // in a React hook. The memo is now just memoisation — same inputs, same
  // dependency array, same result.
  const liveAllPlayers = useMemo(
    () => liveAllPlayersOf({ allPlayers, tournament, pausedTournaments }),
    [allPlayers, tournament, pausedTournaments],
  );

  const effectivePlayerDB = useMemo(
    // The demo bank only where it is the club's own data (#1060).
    () => getEffectivePlayerDB(archive, liveAllPlayers, seedFallbackPlayers(PLAYER_DB)),
    [archive, liveAllPlayers],
  );

  // Combined archive: all tournaments + a virtual "single-matches tournament"
  // that contains all registered single matches, dated by the latest match.
  // Used in archive view, player profile and statistics.
  const combinedArchive = useMemo(() => {
    const items = [...archive];

    // The virtual single-matches entry now lives in @starpoint/engine, because
    // the API builds the same archive to compute the same member history.
    const singles = buildSingleMatchesArchiveEntry(singleMatches, allPlayers);
    if (singles) items.push(singles);

    // Sort: newest first (based on archivedAt).
    //
    // `?.` IS NOT DEFENSIVE PROGRAMMING FOR ITS OWN SAKE. An archive entry with
    // no `data` reached this and threw inside `Array.sort`, inside a `useMemo` —
    // which React cannot recover from, so the member app rendered a WHITE SCREEN
    // for every member who had played a tournament. The engine's own
    // `getPlayerTournamentHistory` already skips such an entry (`if (!t) return`)
    // rather than trusting the shape; this is the same rule one layer up.
    //
    // The entry that caused it is fixed at its source (`useMemberBootstrap` no
    // longer puts the member's computed history into the archive), so nothing
    // should produce one today. This stays because «one malformed row blanks the
    // entire application» is the wrong failure mode whatever produced the row.
    items.sort((a, b) => (b.data?.archivedAt || 0) - (a.data?.archivedAt || 0));
    return items;
  }, [archive, singleMatches, allPlayers]);

  // Archive extended with active + paused tournaments.
  // Used by PlayerHistoryModal so that "Full historikk" includes ongoing
  // and parked tournaments — not just fully archived ones.
  const archiveWithLive = useMemo(() => {
    const list = [...combinedArchive];
    // Active tournament (not archived yet)
    if (tournament && !tournament.isArchived) {
      list.unshift({
        key: `active:${tournament.id || 'unknown'}`,
        data: { ...tournament, isLive: true },
      });
    }
    // Active seriespill (own slot) — so seriespill matches show up in the full profile continuously
    if (activeSeries && !activeSeries.isArchived) {
      list.unshift({
        key: `active-series:${activeSeries.id || 'unknown'}`,
        data: { ...activeSeries, isLive: true },
      });
    }
    // Paused tournaments
    pausedTournaments.forEach((pt) => {
      list.unshift({
        key: `paused:${pt.id}`,
        data: { ...pt, isLive: true },
      });
    });
    return list;
  }, [combinedArchive, tournament, pausedTournaments, activeSeries]);

  // The archive view's data source: the archive + virtual single matches + active KM +
  // active seriespill (per class+season). `matchesOnly` entries show up only in
  // the «Kamper» tab (not in the tournament list) — ongoing season/active KM.
  const archiveForArchive = useMemo(() => {
    const items = [...combinedArchive];
    // Active KM (not archived) → the matches go in «Kamper», not in the tournament list.
    if (tournament && tournament.type === 'klubbmesterskap' && !tournament.isArchived) {
      const scored = (tournament.matches || []).filter((m) => m && m.score);
      if (scored.length > 0) {
        const latest = scored.reduce((mx, m) => Math.max(mx, m.playedAt || m.completedAt || 0), 0);
        items.push({
          key: `live-km:${tournament.id || 'x'}`,
          data: {
            ...tournament,
            isLive: true,
            matchesOnly: true,
            archivedAt: latest || Date.now(),
          },
        });
      }
    }
    // Active seriespill → one entry per (class, season). The ongoing season
    // is taken from series.matches (matchesOnly = «Kamper» only). Finished seasons
    // live in the class's seriesHistory[] (the matches are removed from series.matches when
    // the season rolls over) and are shown in the tournament list.
    if (activeSeries) {
      const baseName = activeSeries.config?.name || activeSeries.name || 'Seriespill';
      const curPairs = activeSeries.pairs || [];
      const curMatches = activeSeries.matches || [];
      const mkSeasonEntry = (
        cls: any,
        season: any,
        matches: any,
        pairs: any,
        archivedAt: any,
        matchesOnly: any,
        histIdx: any,
      ) => {
        const seasonName = `${baseName} · ${cls.name} · Sesong ${season}`;
        const seasonClass = { ...cls, currentSeason: season };
        items.push({
          key: `series-${matchesOnly ? 'cur' : 'hist'}:${activeSeries.id || 'x'}:${cls.id}:s${season}`,
          data: {
            ...activeSeries,
            type: 'seriespill',
            name: seasonName,
            isLive: matchesOnly,
            isSeriesSeason: true,
            matchesOnly,
            seasonHistIdx: histIdx != null ? histIdx : null,
            seasonClassId: cls.id,
            archivedAt: archivedAt || Date.now(),
            currentSeason: season,
            classes: [seasonClass],
            pairs: pairs || [],
            matches: matches || [],
            players: activeSeries.players || [],
            config: {
              ...(activeSeries.config || {}),
              name: seasonName,
              tournamentType: 'seriespill',
              classes: [seasonClass],
            },
          },
        });
      };
      (activeSeries.config?.classes || []).forEach((cls) => {
        const curSeason = cls.currentSeason || 1;
        // Ongoing season («Kamper» only)
        const classCur = curMatches.filter((m) => m && m.classId === cls.id);
        const scoredCur = classCur.filter((m) => m.score);
        if (scoredCur.length > 0) {
          const latest = scoredCur.reduce(
            (mx, m) => Math.max(mx, m.playedAt || m.completedAt || 0),
            0,
          );
          mkSeasonEntry(cls, curSeason, classCur, curPairs, latest, true, null);
        }
        // Finished seasons (in seriesHistory) → shown in the tournament list
        (cls.seriesHistory || []).forEach((hist, idx) => {
          const histScored = (hist.matches || []).filter((m) => m && m.score);
          if (histScored.length === 0) return;
          const latest = histScored.reduce(
            (mx, m) => Math.max(mx, m.playedAt || m.completedAt || hist.endedAt || 0),
            0,
          );
          mkSeasonEntry(cls, hist.season, hist.matches, hist.pairs, latest, false, idx);
        });
      });
    }
    return items;
  }, [combinedArchive, tournament, activeSeries]);

  return { liveAllPlayers, effectivePlayerDB, combinedArchive, archiveWithLive, archiveForArchive };
}
